import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDotBotsAuth } from "@dotbots-boutique/auth-sdk/react";
import { useApi } from "../lib/api";
import { formatDate, relativeTime, slaRemaining } from "../lib/format";
import { PriorityBadge, StatusBadge } from "../components/shared/Badge";
import { Avatar } from "../components/shared/Avatar";
import { HtmlMessage } from "../components/shared/HtmlMessage";
import { isHtmlBodyUsable } from "../lib/sanitizeHtml";
import { notifyDialog } from "../components/shared/modals";
import { AddRuleFromTicket } from "../components/shared/AddRuleFromTicket";
import { hasPermission } from "../lib/permissions";
import { useI18n } from "../i18n/I18nProvider";
import type { Agent, Message, SlaPolicy, Team, TicketDetail as TicketDetailT, TicketPriority, TicketStatus } from "@shared/types";

interface TicketResponse {
  ticket: TicketDetailT;
  messages: Message[];
}

const SLA_TOTAL_FALLBACK_HOURS = 24;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function senderLabel(m: Message, t: (k: string) => string): string {
  if (m.is_internal_note) return t("ticketDetail.internalNote");
  if (m.agent_name) return m.agent_name;
  const contact = [m.contact_first_name, m.contact_last_name].filter(Boolean).join(" ");
  if (contact) return contact;
  if (m.sender_name) return m.sender_name;
  return m.direction === "inbound" ? t("ticketDetail.client") : t("ticketDetail.agent");
}

interface NeighborRef {
  id: string;
  ticket_number: number;
}

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // The list page forwards its filters via the URL search so neighbors
  // can walk the same filtered set the operator was browsing. We keep
  // them on the URL when navigating prev/next so the chain doesn't
  // lose the filter context after the first hop.
  const [searchParams] = useSearchParams();
  const listSearch = searchParams.toString();
  const api = useApi();
  const { t, lang } = useI18n();
  const { user, fetch: authFetch } = useDotBotsAuth();
  const roles = user?.roles ?? [];
  // Viewers see ticket detail but can't reply, change properties, or
  // poke the AI helpers. Hide every write surface so the UI matches
  // what the backend would accept.
  const canWriteTicket = hasPermission(roles, "tickets.write");
  const canEditContact = hasPermission(roles, "contacts.write");
  // Rule management lives under the admin role (same gate the
  // AutoArchiveRules page uses). Non-admin agents don't see the
  // button on the ticket toolbar.
  const canManageRules = hasPermission(roles, "agents.write");
  const [showAddRule, setShowAddRule] = useState(false);
  const canUseAi = hasPermission(roles, "ai.use");
  const canWriteKb = hasPermission(roles, "knowledge.write");
  const [data, setData] = useState<TicketResponse | null>(null);
  const [neighbors, setNeighbors] = useState<{ prev: NeighborRef | null; next: NeighborRef | null }>({ prev: null, next: null });
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [slaPolicies, setSlaPolicies] = useState<SlaPolicy[]>([]);
  // Aliases the current agent may pick as the outbound From address
  // for this ticket. Refetched whenever the ticket's team_id changes
  // (via edit-mode) so team_emails for the new team appear.
  const [aliasPool, setAliasPool] = useState<{ agentAliases: string[]; teamAliases: string[] }>({
    agentAliases: [],
    teamAliases: [],
  });
  // Holds the message currently shown in the full-screen expand modal,
  // or null when the modal is closed.
  const [expandedMessage, setExpandedMessage] = useState<Message | null>(null);
  // Per-message translation state. The body itself is persisted on
  // the message row (translated_body / translated_lang), so this map
  // is just an in-memory layer for translations made *this* session
  // before the next reload picks up the saved column. translatedIds
  // tracks which messages render translated right now; translatingIds
  // drives the per-button spinner.
  const [translations, setTranslations] = useState<Map<string, string>>(new Map());
  const [translatedIds, setTranslatedIds] = useState<Set<string>>(new Set());
  const [translatingIds, setTranslatingIds] = useState<Set<string>>(new Set());
  const [translatingReply, setTranslatingReply] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  // Files queued for upload alongside the next reply. Kept as File
  // objects so the multipart upload streams them as-is; we never
  // base64 in the browser.
  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  const [sendError, setSendError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [extractingKb, setExtractingKb] = useState(false);
  const [kbResult, setKbResult] = useState<{ id: string; title: string } | null>(null);
  const [clearingAwaiting, setClearingAwaiting] = useState(false);
  // Marks the AI summary card so we can highlight it briefly after a
  // freshly generated summary lands — the section lives in the aside,
  // which on mobile is below the fold and easy to miss otherwise.
  const [summaryFlash, setSummaryFlash] = useState(false);
  const summaryRef = useRef<HTMLElement | null>(null);
  const replyRef = useRef<HTMLTextAreaElement | null>(null);

  // ESC key closes the expand modal.
  useEffect(() => {
    if (!expandedMessage) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpandedMessage(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [expandedMessage]);

  const load = useCallback(async () => {
    if (!id) return;
    const res = await api.get<TicketResponse>(`/api/tickets/${id}`);
    setData(res);
  }, [api, id]);

  useEffect(() => { load(); }, [load]);

  const [retryingIds, setRetryingIds] = useState<Set<string>>(new Set());
  const retryMessage = async (messageId: string) => {
    if (!id) return;
    setRetryingIds((prev) => new Set(prev).add(messageId));
    try {
      await api.post(`/api/tickets/${id}/messages/${messageId}/retry`, {});
    } catch (error) {
      void notifyDialog({ message: t("ticketDetail.retryFailed", { message: (error as Error).message }) });
    } finally {
      await load();
      setRetryingIds((prev) => {
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
    }
  };

  // Auto-refresh while the ticket is open so a customer reply that
  // lands via IMAP/webhook surfaces without the operator having to
  // hit refresh. Paused when the tab is hidden — when it comes back
  // we fire an immediate load() so anything that arrived during the
  // pause shows up right away, and then resume the regular cadence.
  // Doesn't refetch /neighbors (those only change when the ticket id
  // or list filters change).
  useEffect(() => {
    if (!id) return;
    const POLL_INTERVAL_MS = 20_000;
    let timer: number | null = null;
    const start = () => {
      if (timer != null) return;
      timer = window.setInterval(() => { void load(); }, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (timer == null) return;
      window.clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        void load();
        start();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [id, load]);

  // Prev/next navigation. Fetched separately so it refreshes when the
  // route id changes (e.g. after clicking one of the buttons) without
  // waiting on the heavier ticket payload. Forwarding listSearch makes
  // /neighbors apply the same WHERE+ORDER the list used.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const url = listSearch
      ? `/api/tickets/${id}/neighbors?${listSearch}`
      : `/api/tickets/${id}/neighbors`;
    api.get<{ prev: NeighborRef | null; next: NeighborRef | null }>(url)
      .then((res) => { if (!cancelled) setNeighbors(res); })
      .catch(() => { if (!cancelled) setNeighbors({ prev: null, next: null }); });
    return () => { cancelled = true; };
  }, [api, id, listSearch]);

  useEffect(() => {
    (async () => {
      const [a, tm, s] = await Promise.all([
        api.get<{ data: Agent[] }>("/api/agents"),
        api.get<{ data: Team[] }>("/api/teams"),
        api.get<{ data: SlaPolicy[] }>("/api/sla-policies"),
      ]);
      setAgents(a.data);
      setTeams(tm.data);
      setSlaPolicies(s.data);
    })().catch(() => {});
  }, [api]);

  // Refresh the alias pool whenever the ticket's team changes so the
  // sidebar dropdown includes team_emails for the currently-set team.
  // `data` is the raw response state; the `ticket` alias isn't
  // destructured until further down (past the loading guard).
  const teamIdForAliases = data?.ticket?.team_id ?? null;
  useEffect(() => {
    let cancelled = false;
    const qs = teamIdForAliases ? `?teamId=${encodeURIComponent(teamIdForAliases)}` : "";
    api
      .get<{ agentAliases: string[]; teamAliases: string[] }>(`/api/me/aliases${qs}`)
      .then((res) => { if (!cancelled) setAliasPool(res); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api, teamIdForAliases]);

  const send = async () => {
    if (!id || !reply.trim()) return;
    setSaving(true);
    setSendError(null);
    try {
      if (replyAttachments.length > 0) {
        // Multipart upload so the files stream as separate parts
        // instead of getting base64-inflated inside a JSON body.
        const form = new FormData();
        form.append("body", reply);
        form.append("isInternalNote", internal ? "true" : "false");
        form.append("channel", internal ? "internal" : "email");
        for (const file of replyAttachments) form.append("attachments", file, file.name);
        await api.postForm(`/api/tickets/${id}/messages`, form);
      } else {
        await api.post(`/api/tickets/${id}/messages`, {
          body: reply,
          isInternalNote: internal,
          channel: internal ? "internal" : "email",
        });
      }
      setReply("");
      setInternal(false);
      setReplyAttachments([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
    } catch (err) {
      setSendError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setReplyAttachments((prev) => [...prev, ...files]);
    // Reset the input so picking the same file twice in a row still
    // triggers onChange — browsers normally skip a re-selection.
    e.target.value = "";
  };

  const removeAttachment = (index: number) => {
    setReplyAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const downloadAttachment = async (att: { id: string; filename: string }) => {
    if (!id) return;
    try {
      const response = await authFetch(
        `${import.meta.env.VITE_BACKEND_URL.replace(/\/$/, "")}/api/tickets/${encodeURIComponent(id)}/attachments/${encodeURIComponent(att.id)}`,
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      // Stream into a Blob via response.blob() — the response is
      // already in the network buffer, this just packages it for the
      // download anchor. Avoids loading the whole bytes into a single
      // ArrayBuffer manually.
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke a moment later so the click had time to commit; some
      // browsers cancel the download if we revoke immediately.
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      void notifyDialog({ message: t("ticketDetail.attachmentDownloadFailed", { message: (err as Error).message }) });
    }
  };

  const suggestReply = async () => {
    if (!id) return;
    setLoadingSuggestion(true);
    try {
      const res = await api.post<{ suggestion: string }>("/api/ai/suggest-reply", { ticketId: id, language: lang });
      setReply(res.suggestion);
    } catch (error) {
      void notifyDialog({ message: t("ticketDetail.aiSuggestionFailed", { message: (error as Error).message }) });
    } finally {
      setLoadingSuggestion(false);
    }
  };

  // What text to render when translation is shown — prefer the
  // persisted column (so a refresh keeps the translation visible
  // without another AI round trip), fall back to the in-session
  // cache populated by the toggle button.
  const translationFor = (m: Message): string | null => {
    if (m.translated_body && m.translated_lang === lang) return m.translated_body;
    return translations.get(m.id) ?? null;
  };

  // Toggle Original ↔ Translation for one message. force=true sends
  // force to the backend so it re-runs the AI call and overwrites
  // the saved translated_body even when one already exists.
  const toggleTranslateMessage = async (m: Message, force = false) => {
    const isCurrentlyTranslated = translatedIds.has(m.id);
    if (isCurrentlyTranslated && !force) {
      setTranslatedIds((prev) => {
        const next = new Set(prev);
        next.delete(m.id);
        return next;
      });
      return;
    }

    const haveCached = !force && translationFor(m) !== null;
    if (!haveCached && m.body && m.body.trim().length > 0) {
      setTranslatingIds((prev) => new Set(prev).add(m.id));
      try {
        const res = await api.post<{ translation: string }>(`/api/messages/${m.id}/translate`, {
          targetLanguage: lang,
          force,
        });
        setTranslations((prev) => new Map(prev).set(m.id, res.translation));
        // Reload the ticket so the persisted translated_body is
        // visible to subsequent renders without keeping it in
        // local-only state.
        await load();
      } catch (error) {
        void notifyDialog({ message: t("ticketDetail.aiTranslateFailed", { message: (error as Error).message }) });
        setTranslatingIds((prev) => {
          const next = new Set(prev);
          next.delete(m.id);
          return next;
        });
        return;
      } finally {
        setTranslatingIds((prev) => {
          const next = new Set(prev);
          next.delete(m.id);
          return next;
        });
      }
    }
    setTranslatedIds((prev) => new Set(prev).add(m.id));
  };

  // Translate the current reply draft back into the language of the
  // ticket's first inbound message — the customer's language. The
  // backend handles detection so we don't have to round-trip twice.
  const translateReplyToTicket = async () => {
    if (!id || !reply.trim()) return;
    setTranslatingReply(true);
    try {
      const res = await api.post<{ translation: string }>("/api/ai/translate-to-ticket", {
        text: reply,
        ticketId: id,
      });
      if (res.translation) setReply(res.translation);
    } catch (error) {
      void notifyDialog({ message: t("ticketDetail.aiTranslateFailed", { message: (error as Error).message }) });
    } finally {
      setTranslatingReply(false);
    }
  };

  const summarize = async () => {
    if (!id || summarizing) return;
    setSummarizing(true);
    try {
      await api.post<{ summary: string }>("/api/ai/summarize", { ticketId: id, language: lang });
      await load();
      // Scroll the freshly-generated summary card into view and flash a
      // background highlight so the operator notices the result on
      // mobile (the AI summary lives in the aside, which is below the
      // fold there).
      setSummaryFlash(true);
      setTimeout(() => {
        summaryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      setTimeout(() => setSummaryFlash(false), 2400);
    } catch (error) {
      void notifyDialog({ message: t("ticketDetail.aiSummaryFailed", { message: (error as Error).message }) });
    } finally {
      setSummarizing(false);
    }
  };

  const extractKnowledgeArticle = async () => {
    if (!id || extractingKb) return;
    setExtractingKb(true);
    setKbResult(null);
    try {
      const res = await api.post<{ id: string; title: string; content: string; tags: string[] }>(
        `/api/tickets/${id}/extract-knowledge`,
        { language: lang },
      );
      setKbResult({ id: res.id, title: res.title });
    } catch (error) {
      void notifyDialog({ message: t("ticketDetail.kbExtractFailed", { message: (error as Error).message }) });
    } finally {
      setExtractingKb(false);
    }
  };

  const updateField = async (patch: Record<string, unknown>) => {
    if (!id) return;
    try {
      await api.patch(`/api/tickets/${id}`, patch);
      await load();
    } catch (error) {
      void notifyDialog({ message: (error as Error).message });
    }
  };

  const focusReply = () => {
    replyRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    replyRef.current?.focus();
  };

  const clearAwaiting = async () => {
    if (!id || clearingAwaiting) return;
    setClearingAwaiting(true);
    try {
      await api.del(`/api/tickets/${id}/awaiting-reply`);
      await load();
    } catch (error) {
      void notifyDialog({ message: (error as Error).message });
    } finally {
      setClearingAwaiting(false);
    }
  };

  const cancelTicket = async () => {
    if (!id || !data) return;
    try {
      await api.patch(`/api/tickets/${id}`, { status: "canceled" });
      await load();
    } catch (error) {
      void notifyDialog({ message: (error as Error).message });
    }
  };

  const slaProgress = useMemo(() => {
    if (!data?.ticket.sla_due_at || !data.ticket.created_at) return null;
    const created = new Date(data.ticket.created_at).getTime();
    const due = new Date(data.ticket.sla_due_at).getTime();
    const total = Math.max(due - created, SLA_TOTAL_FALLBACK_HOURS * 60 * 60 * 1000);
    const elapsed = Math.max(0, Math.min(total, Date.now() - created));
    return Math.round((elapsed / total) * 100);
  }, [data?.ticket.sla_due_at, data?.ticket.created_at]);

  if (!data) return <p>{t("common.loading")}</p>;
  const { ticket, messages } = data;
  const sla = slaRemaining(ticket.sla_due_at, ticket.status);
  const contactName = [ticket.contact_first_name, ticket.contact_last_name].filter(Boolean).join(" ") || ticket.contact_email || "—";
  // The hint the "Auto-archive rule" modal pre-fills from. We take
  // the first inbound message as the canonical source, falling back
  // to the contact email + ticket subject when there's no inbound
  // yet (an agent-created ticket won't surface the button anyway,
  // because without an email in play auto-archive isn't meaningful).
  const firstInbound = messages.find((m) => m.direction === "inbound" && !m.is_internal_note);
  const firstInboundHint = firstInbound
    ? { fromEmail: ticket.contact_email ?? null, subject: ticket.subject ?? null }
    : null;
  // KB extract is only meaningful once the conversation is wrapped up.
  // Hide the button on new/working/waiting tickets so we don't seed
  // half-baked drafts into the knowledge base.
  const ticketIsClosable = ticket.status === "resolved" || ticket.status === "closed";
  // Mirror the isAwaitingReply rule from the list (TicketList.tsx) — a
  // dismiss stamps awaiting_reply_cleared_at; the badge returns the
  // moment a fresh inbound bumps last_message_at past it.
  const showAwaiting = ticket.last_message_direction === "inbound"
    && ticket.status !== "closed" && ticket.status !== "canceled"
    && !(ticket.awaiting_reply_cleared_at && ticket.last_message_at
         && ticket.awaiting_reply_cleared_at >= ticket.last_message_at);

  return (
    <div className="ticket-detail">
      <div className="ticket-detail-main">

        <header className="ticket-header card">
          <div className="ticket-header-meta">
            <span className="ticket-header-num">#{ticket.ticket_number}</span>
            <StatusBadge status={ticket.status} />
            <PriorityBadge priority={ticket.priority} />
            {ticket.is_private && <span className="badge role-badge">{t("tickets.privateBadge")}</span>}
            {showAwaiting && (
              <span className="badge badge-awaiting" title={t("ticketDetail.awaitingReply")}>
                {t("ticketList.awaitingReply")}
                {canWriteTicket && (
                  <span
                    className="badge-awaiting-clear"
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); void clearAwaiting(); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        void clearAwaiting();
                      }
                    }}
                    title={t("ticketDetail.awaitingReplyClear")}
                    aria-label={t("ticketDetail.awaitingReplyClear")}
                    aria-disabled={clearingAwaiting}
                  >✕</span>
                )}
              </span>
            )}
          </div>
          <div className="ticket-toolbar">
            <div className="ticket-toolbar-group ticket-nav">
              <button
                onClick={() => neighbors.prev && navigate({ pathname: `/tickets/${neighbors.prev.id}`, search: listSearch })}
                disabled={!neighbors.prev}
                title={neighbors.prev ? `#${neighbors.prev.ticket_number}` : undefined}
              >
                ← {t("ticketDetail.prevTicket")}
              </button>
              <button
                onClick={() => neighbors.next && navigate({ pathname: `/tickets/${neighbors.next.id}`, search: listSearch })}
                disabled={!neighbors.next}
                title={neighbors.next ? `#${neighbors.next.ticket_number}` : undefined}
              >
                {t("ticketDetail.nextTicket")} →
              </button>
              <button onClick={() => { void load(); }} title={t("common.refresh")}>
                ⟳ {t("common.refresh")}
              </button>
            </div>
            <div className="ticket-toolbar-group ticket-actions">
              {ticket.status !== "canceled" && canWriteTicket && (
                <button onClick={cancelTicket}>{t("ticketDetail.archive")}</button>
              )}
              {canManageRules && firstInboundHint && (
                <button type="button" onClick={() => setShowAddRule(true)} title={t("autoArchive.addFromTicketTitle")}>
                  {t("autoArchive.addFromTicketShort")}
                </button>
              )}
              {canWriteTicket && (
                <button className="primary" onClick={focusReply}>{t("ticketDetail.replyShort")}</button>
              )}
            </div>
          </div>
          <div className="ticket-header-row">
            <h2 className="ticket-header-subject">{ticket.subject}</h2>
          </div>
        </header>

        <div className="card">
          {messages.map((m, idx) => {
            const isLast = idx === messages.length - 1;
            const sender = senderLabel(m, t);
            const showDelivery = m.direction === "outbound" && !m.is_internal_note && (m.delivery_status || m.channel === "email");
            const status = m.delivery_status;
            const isTranslated = translatedIds.has(m.id);
            const isTranslating = translatingIds.has(m.id);
            const translatedBody = isTranslated ? translationFor(m) : null;
            const hasSavedTranslation = translationFor(m) !== null;
            return (
              <article key={m.id} className={`thread-row ${m.is_internal_note ? "internal-note" : m.direction}${isLast ? " thread-last" : ""}`}>
                <Avatar name={sender} />
                <div className="thread-bubble-wrap">
                  <div className="thread-meta">
                    <div className="thread-meta-left">
                      <span className="thread-sender">{sender}</span>
                      {isLast && <span className="thread-latest-badge">{t("ticketDetail.latestBadge")}</span>}
                    </div>
                    <span className="thread-time" title={formatDate(m.created_at)}>{relativeTime(m.created_at)}</span>
                  </div>
                  {m.direction === "inbound" && m.channel === "email" && m.email_to_addresses && m.email_to_addresses.length > 0 && (
                    <div className="thread-to" title={m.email_to_addresses.join(", ")}>
                      {t("ticketDetail.sentTo")}: {m.email_to_addresses.join(", ")}
                    </div>
                  )}
                  <div className={`thread-bubble ${m.is_internal_note ? "internal-note" : m.direction}`}>
                    <div className="thread-bubble-actions">
                      {canUseAi && m.body && m.body.trim().length > 0 && (
                        <>
                          <button
                            type="button"
                            className="thread-bubble-action"
                            aria-label={isTranslated ? t("ticketDetail.showOriginal") : t("ticketDetail.translateToMyLang")}
                            title={isTranslated ? t("ticketDetail.showOriginal") : t("ticketDetail.translateToMyLang")}
                            aria-pressed={isTranslated}
                            disabled={isTranslating}
                            onClick={() => toggleTranslateMessage(m, false)}
                          >
                            {isTranslating ? "…" : isTranslated ? "↺" : "⇄"}
                          </button>
                          {hasSavedTranslation && (
                            <button
                              type="button"
                              className="thread-bubble-action"
                              aria-label={t("ticketDetail.retranslate")}
                              title={t("ticketDetail.retranslate")}
                              disabled={isTranslating}
                              onClick={() => toggleTranslateMessage(m, true)}
                            >
                              ⟳
                            </button>
                          )}
                        </>
                      )}
                      <button
                        type="button"
                        className="thread-bubble-action"
                        aria-label={t("ticketDetail.expandMessage")}
                        title={t("ticketDetail.expandMessage")}
                        onClick={() => setExpandedMessage(m)}
                      >
                        ⤢
                      </button>
                      {m.direction === "outbound" && !m.is_internal_note && (
                        <button
                          type="button"
                          className="thread-bubble-action"
                          aria-label={t("ticketDetail.refreshMessage")}
                          title={t("ticketDetail.refreshMessage")}
                          onClick={() => { void load(); }}
                        >
                          ⟳
                        </button>
                      )}
                    </div>
                    {translatedBody
                      ? <div className="message-body">{translatedBody}</div>
                      : isHtmlBodyUsable(m.body_html)
                      ? <HtmlMessage html={m.body_html!} />
                      : <div className="message-body">{m.body}</div>}
                    {m.attachments && m.attachments.length > 0 && (
                      <ul className="message-attachments">
                        {m.attachments.map((att) => (
                          <li key={att.id} className="message-attachment">
                            <button
                              type="button"
                              className="message-attachment-link"
                              onClick={() => downloadAttachment(att)}
                              title={att.filename}
                            >
                              📎 <span className="message-attachment-name">{att.filename}</span>
                              <small>· {formatBytes(att.size_bytes)}</small>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {showDelivery && (
                      <div className="thread-delivery">
                        {status && <span className={`delivery-badge ${status}`}>{t(`delivery.${status}`)}</span>}
                        {m.delivered_at && status === "delivered" && (
                          <span title={formatDate(m.delivered_at)}>· {relativeTime(m.delivered_at)}</span>
                        )}
                        {m.opened_at && status === "opened" && (
                          <span title={formatDate(m.opened_at)}>· {relativeTime(m.opened_at)}</span>
                        )}
                        {m.provider === "smtp" && status === "sent" && (
                          <span>· {t("delivery.smtpHint")}</span>
                        )}
                        {/* Per-message delivery timeline. Each row is one
                            transition the provider reported back. */}
                        <ul className="delivery-history">
                          <li>
                            <span className="delivery-history-label">{t("delivery.queued")}</span>
                            <span className="delivery-history-time" title={formatDate(m.created_at)}>{formatDate(m.created_at)}</span>
                          </li>
                          {m.sent_at && (
                            <li>
                              <span className="delivery-history-label">{t("delivery.sent")}</span>
                              <span className="delivery-history-time" title={formatDate(m.sent_at)}>{formatDate(m.sent_at)}</span>
                            </li>
                          )}
                          {m.delivered_at && (
                            <li>
                              <span className="delivery-history-label">{t("delivery.delivered")}</span>
                              <span className="delivery-history-time" title={formatDate(m.delivered_at)}>{formatDate(m.delivered_at)}</span>
                            </li>
                          )}
                          {m.opened_at && (
                            <li>
                              <span className="delivery-history-label">{t("delivery.opened")}</span>
                              <span className="delivery-history-time" title={formatDate(m.opened_at)}>{formatDate(m.opened_at)}</span>
                            </li>
                          )}
                          {/* Show the final non-progress status (bounce/
                              complain/fail/delay) only when it isn't
                              already represented by a queued/sent/delivered/
                              opened row above. */}
                          {status && status !== "queued" && status !== "sent" && status !== "delivered" && status !== "opened" && (
                            <li>
                              <span className="delivery-history-label">{t(`delivery.${status}`)}</span>
                              <span className="delivery-history-time" title={formatDate(m.created_at)}>—</span>
                            </li>
                          )}
                        </ul>
                        {m.delivery_error && (
                          <div className="delivery-error" style={{ width: "100%" }}>
                            {t("delivery.errorPrefix")}: {m.delivery_error}
                          </div>
                        )}
                        {m.channel === "email" && status === "failed" && canWriteTicket && (
                          <button
                            type="button"
                            className="thread-bubble-retry"
                            disabled={retryingIds.has(m.id)}
                            onClick={() => { void retryMessage(m.id); }}
                          >
                            {retryingIds.has(m.id) ? "…" : `↻ ${t("ticketDetail.retryMessage")}`}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
          {messages.length === 0 && <p style={{ color: "var(--text-muted)" }}>{t("ticketDetail.noMessages")}</p>}
        </div>

        {canWriteTicket && (
          <div className="card reply-card">
            <div className="reply-toolbar">
              {canUseAi && (
                <>
                  <button onClick={suggestReply} disabled={loadingSuggestion}>
                    {loadingSuggestion ? t("ticketDetail.aiThinking") : t("ticketDetail.aiSuggestReply")}
                  </button>
                  <button onClick={summarize} disabled={summarizing}>
                    {summarizing ? t("ticketDetail.aiThinking") : t("ticketDetail.aiSummary")}
                  </button>
                  <button
                    type="button"
                    onClick={translateReplyToTicket}
                    disabled={translatingReply || !reply.trim()}
                    title={t("ticketDetail.translateReplyHint")}
                  >
                    {translatingReply ? t("ticketDetail.translating") : t("ticketDetail.translateReplyToTicket")}
                  </button>
                </>
              )}
              <label className="reply-internal-toggle">
                <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
                {t("ticketDetail.internalNote")}
              </label>
            </div>
            <textarea
              ref={replyRef}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder={internal ? t("ticketDetail.notePlaceholder") : t("ticketDetail.replyPlaceholder")}
              rows={5}
            />
            <div className="reply-attachments">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={onPickFiles}
                style={{ display: "none" }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
              >
                📎 {t("ticketDetail.attachFiles")}
              </button>
              {replyAttachments.length > 0 && (
                <ul className="reply-attachment-list">
                  {replyAttachments.map((file, i) => (
                    <li key={`${file.name}-${i}`} className="reply-attachment-chip">
                      <span title={file.name}>{file.name}</span>
                      <small>{formatBytes(file.size)}</small>
                      <button
                        type="button"
                        aria-label={t("ticketDetail.attachmentRemove")}
                        onClick={() => removeAttachment(i)}
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {sendError && (
              <div style={{ color: "var(--danger)", fontSize: 13, marginTop: 6 }}>{sendError}</div>
            )}
            <div className="reply-actions">
              <button className="primary" disabled={saving || !reply.trim()} onClick={send}>
                {saving
                  ? t("ticketDetail.sending")
                  : internal ? t("ticketDetail.saveNote") : t("ticketDetail.send")}
              </button>
            </div>
          </div>
        )}
      </div>

      <aside className="ticket-detail-aside">
        <section className="card">
          <h3 className="aside-heading">{t("ticketDetail.customerHeading")}</h3>
          <div className="customer-card">
            <Avatar name={contactName} size={44} />
            <div className="customer-card-info">
              <div className="customer-card-name">{contactName}</div>
              {ticket.contact_email && <div className="customer-card-email">{ticket.contact_email}</div>}
              {ticket.contact_phone && <div className="customer-card-phone">{ticket.contact_phone}</div>}
            </div>
          </div>
          {ticket.contact_id && canEditContact && (
            <div style={{ marginTop: 12 }}>
              <Link
                to={`/contacts?open=${ticket.contact_id}&edit=1`}
                className="customer-card-action"
              >
                {t("ticketDetail.editCustomer")}
              </Link>
            </div>
          )}
        </section>

        <section className="card">
          <h3 className="aside-heading">{t("ticket.sla")}</h3>
          <div className={`sla-readout sla-${sla.state}`}>{sla.label}</div>
          {slaProgress !== null && (
            <div className="sla-bar">
              <div
                className={`sla-bar-fill sla-${sla.state}-bg`}
                style={{ width: `${Math.min(100, slaProgress)}%` }}
              />
            </div>
          )}
          <div className="aside-fineprint">
            {ticket.sla_policy_name ?? t("ticketDetail.noSlaPolicy")}
          </div>
          <div className="aside-fineprint">
            {t("ticketDetail.deadline")}: {formatDate(ticket.sla_due_at)}
          </div>
        </section>

        {/* Properties: editable for agents/admins, read-only summary for
            viewers (so they can still see who owns the ticket without
            being able to reassign it). */}
        <section className="card">
          <h3 className="aside-heading">{t("ticketDetail.properties")}</h3>
          {canWriteTicket ? (
            <>
              <div className="field">
                <label>{t("ticket.status")}</label>
                <select value={ticket.status} onChange={(e) => updateField({ status: e.target.value as TicketStatus })}>
                  <option value="new">{t("status.new")}</option>
                  <option value="working">{t("status.working")}</option>
                  <option value="waiting_feedback">{t("status.waiting_feedback")}</option>
                  <option value="resolved">{t("status.resolved")}</option>
                  <option value="closed">{t("status.closed")}</option>
                  <option value="canceled">{t("status.canceled")}</option>
                </select>
              </div>
              <div className="field">
                <label>{t("ticket.priority")}</label>
                <select value={ticket.priority} onChange={(e) => updateField({ priority: e.target.value as TicketPriority })}>
                  <option value="critical">{t("priority.critical")}</option>
                  <option value="high">{t("priority.high")}</option>
                  <option value="medium">{t("priority.medium")}</option>
                  <option value="low">{t("priority.low")}</option>
                </select>
              </div>
              <div className="field">
                <label>{t("ticket.assignee")}</label>
                <select value={ticket.assignee_id ?? ""} onChange={(e) => updateField({ assigneeId: e.target.value || null })}>
                  <option value="">{t("ticketDetail.noAssignee")}</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label>{t("ticket.team")}</label>
                <select value={ticket.team_id ?? ""} onChange={(e) => updateField({ teamId: e.target.value || null })}>
                  <option value="">—</option>
                  {teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label>SLA</label>
                <select value={ticket.sla_policy_id ?? ""} onChange={(e) => updateField({ slaPolicyId: e.target.value || null })}>
                  <option value="">{t("ticketDetail.noSlaPolicy")}</option>
                  {slaPolicies.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label className="inline-checkbox">
                  <input
                    type="checkbox"
                    checked={!!ticket.is_private}
                    onChange={(e) => updateField({ isPrivate: e.target.checked })}
                  />
                  {t("tickets.privateField")}
                </label>
                <small style={{ color: "var(--text-muted)", display: "block", marginTop: 4 }}>{t("tickets.privateHelp")}</small>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>{t("tickets.inboundAliasField")}</label>
                <select
                  value={ticket.inbound_alias ?? ""}
                  onChange={(e) => updateField({ inboundAlias: e.target.value || null })}
                >
                  <option value="">{t("tickets.inboundAliasDefault")}</option>
                  {aliasPool.agentAliases.length > 0 && (
                    <optgroup label={t("tickets.inboundAliasPersonalGroup")}>
                      {aliasPool.agentAliases.map((email) => (
                        <option key={`me-${email}`} value={email}>{email}</option>
                      ))}
                    </optgroup>
                  )}
                  {aliasPool.teamAliases.length > 0 && (
                    <optgroup label={t("tickets.inboundAliasTeamGroup")}>
                      {aliasPool.teamAliases
                        .filter((email) => !aliasPool.agentAliases.includes(email))
                        .map((email) => (
                          <option key={`team-${email}`} value={email}>{email}</option>
                        ))}
                    </optgroup>
                  )}
                </select>
                <small style={{ color: "var(--text-muted)", display: "block", marginTop: 4 }}>{t("tickets.inboundAliasHelp")}</small>
              </div>
            </>
          ) : (
            <ul className="stats-list">
              <li><span>{t("ticket.status")}</span><span><StatusBadge status={ticket.status} /></span></li>
              <li><span>{t("ticket.priority")}</span><span><PriorityBadge priority={ticket.priority} /></span></li>
              <li><span>{t("ticket.assignee")}</span><span>{ticket.assignee_name ?? t("ticketDetail.noAssignee")}</span></li>
              <li><span>{t("ticket.team")}</span><span>{ticket.team_name ?? "—"}</span></li>
              <li><span>SLA</span><span>{ticket.sla_policy_name ?? t("ticketDetail.noSlaPolicy")}</span></li>
            </ul>
          )}
        </section>

        {ticket.ai_summary && (
          <section
            ref={summaryRef}
            className={`card${summaryFlash ? " ai-summary-flash" : ""}`}
          >
            <h3 className="aside-heading">{t("ticketDetail.aiSummaryHeading")}</h3>
            <div>{ticket.ai_summary}</div>
          </section>
        )}

        {canWriteKb && canUseAi && ticketIsClosable && (
          <section className="card">
            <h3 className="aside-heading">{t("ticketDetail.kbHeading")}</h3>
            <p style={{ marginTop: 0, color: "var(--text-muted)", fontSize: 13 }}>
              {t("ticketDetail.kbHelp")}
            </p>
            <button onClick={extractKnowledgeArticle} disabled={extractingKb}>
              {extractingKb ? t("ticketDetail.kbExtracting") : t("ticketDetail.kbExtract")}
            </button>
            {kbResult && (
              <div style={{ marginTop: 10, fontSize: 13 }}>
                <div style={{ color: "var(--ok)", marginBottom: 4 }}>
                  {t("ticketDetail.kbCreated")}
                </div>
                <Link to={`/knowledge?open=${kbResult.id}`}>{kbResult.title}</Link>
              </div>
            )}
          </section>
        )}

        <section className="card">
          <h3 className="aside-heading">{t("ticketDetail.statsHeading")}</h3>
          <ul className="stats-list">
            <li><span>{t("ticket.created")}</span><span>{formatDate(ticket.created_at)}</span></li>
            <li><span>{t("ticket.firstResponse")}</span><span>{ticket.first_response_at ? formatDate(ticket.first_response_at) : "—"}</span></li>
            <li><span>{t("ticket.resolved")}</span><span>{ticket.resolved_at ? formatDate(ticket.resolved_at) : "—"}</span></li>
            <li><span>{t("ticket.closed")}</span><span>{ticket.closed_at ? formatDate(ticket.closed_at) : "—"}</span></li>
            <li><span>{t("ticket.reopens")}</span><span>{ticket.reopen_count}</span></li>
            {ticket.csat != null && <li><span>{t("ticket.csat")}</span><span>{ticket.csat}/5</span></li>}
          </ul>
        </section>
      </aside>

      {expandedMessage && (
        <div
          className="message-modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            // Click on the backdrop (not the panel) closes the modal.
            if (e.target === e.currentTarget) setExpandedMessage(null);
          }}
        >
          <div className="message-modal-panel">
            <header className="message-modal-header">
              <div className="message-modal-meta">
                <div className="message-modal-sender">{senderLabel(expandedMessage, t)}</div>
                <div className="message-modal-time">{formatDate(expandedMessage.created_at)}</div>
              </div>
              <button
                type="button"
                className="message-modal-close"
                aria-label={t("common.close")}
                onClick={() => setExpandedMessage(null)}
              >
                ✕
              </button>
            </header>
            <div className="message-modal-body">
              {isHtmlBodyUsable(expandedMessage.body_html)
                ? <HtmlMessage html={expandedMessage.body_html!} />
                : <div className="message-body">{expandedMessage.body}</div>}
            </div>
          </div>
        </div>
      )}
      {showAddRule && firstInboundHint && (
        <AddRuleFromTicket
          ticketId={ticket.id}
          defaults={firstInboundHint}
          onClose={() => setShowAddRule(false)}
          onCreated={() => { void load(); }}
        />
      )}
    </div>
  );
}
